import React, { useState, useEffect } from 'react';
import { clinicorpApi } from '../services/api';
import toast from 'react-hot-toast';

const fmtBRL = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);

const fmtPct = (v) =>
  `${(Number(v) * 100).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

const fmtNum = (v) => Number(v || 0).toLocaleString('pt-BR');

const isNumericKey = (key) =>
  /valu|valor|pric|preco|receita|total|amount|custo|cost/i.test(key);

const formatCell = (key, value) => {
  if (value === null || value === undefined) return '—';
  if (isNumericKey(key) && !isNaN(parseFloat(value))) return fmtBRL(parseFloat(value));
  return String(value);
};

const exportCSV = (columns, rows, filename) => {
  const header = columns.join(';');
  const body = rows.map(r => columns.map(c => {
    const v = r[c];
    if (v === null || v === undefined) return '';
    const s = String(v).replace(/"/g, '""');
    return s.includes(';') || s.includes('\n') ? `"${s}"` : s;
  }).join(';')).join('\n');
  const bom = '﻿';
  const blob = new Blob([bom + header + '\n' + body], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const today = () => new Date().toISOString().split('T')[0];
const monthStart = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
};

/* ─────────── Unit card component ─────────── */
const UnitCard = ({ unit }) => {
  const conv = Number(unit.ConversionRate || 0) * 100;
  const appTotal = Number(unit.AppointmentsTotal || 0);
  const appFinished = Number(unit.AppoinmentsFinished || 0);
  const appMissed = Number(unit.AppointmentsMissed || 0);
  const appNew = Number(unit.AppointmentsNewPatients || 0);
  const appExisting = Number(unit.AppointmentsExistingPatients || 0);

  const estTotal = Number(unit.EstimatesTotalQuantity || 0);
  const estApproved = Number(unit.EstimatesApprovedQuantity || 0);
  const estOpen = Number(unit.EstimatesOpenQuantity || 0);
  const estFollowUp = Number(unit.EstimatesFollowUpQuantity || 0);
  const estRejected = Number(unit.EstimatesRejectedQuantity || 0);

  const revenue = Number(unit.TotalRevenueAmount || 0);
  const received = Number(unit.TotalReceivedAmount || 0);
  const expenses = Number(unit.TotalExpenses || 0);
  const result = received - expenses;
  const ticket = Number(unit.ApprovedTicketAverage || 0);

  const convColor = conv >= 20 ? 'text-emerald-600 bg-emerald-50' :
                    conv >= 10 ? 'text-amber-600 bg-amber-50' : 'text-red-600 bg-red-50';

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
      {/* Card header */}
      <div className="px-5 py-4 bg-gradient-to-r from-slate-800 to-slate-700 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className="material-symbols-outlined text-white/60 text-[20px] shrink-0">local_hospital</span>
          <h3 className="font-headline font-bold text-white text-sm truncate">{unit.UnityName}</h3>
        </div>
        <span className={`shrink-0 text-[11px] font-bold px-2.5 py-1 rounded-full ${convColor}`}>
          Conv. {conv.toFixed(1)}%
        </span>
      </div>

      <div className="p-5 space-y-5">
        {/* Revenue trio */}
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center p-3 bg-slate-50 rounded-xl">
            <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-1">Faturamento</p>
            <p className="font-headline font-black text-sm text-slate-800">{fmtBRL(revenue)}</p>
          </div>
          <div className="text-center p-3 bg-emerald-50 rounded-xl">
            <p className="text-[9px] font-bold uppercase tracking-widest text-emerald-500 mb-1">Recebido</p>
            <p className="font-headline font-black text-sm text-emerald-700">{fmtBRL(received)}</p>
          </div>
          <div className={`text-center p-3 rounded-xl ${result >= 0 ? 'bg-blue-50' : 'bg-red-50'}`}>
            <p className={`text-[9px] font-bold uppercase tracking-widest mb-1 ${result >= 0 ? 'text-blue-500' : 'text-red-400'}`}>Resultado</p>
            <p className={`font-headline font-black text-sm ${result >= 0 ? 'text-blue-700' : 'text-red-600'}`}>{fmtBRL(result)}</p>
          </div>
        </div>

        {/* Expenses + ticket */}
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl">
            <span className="material-symbols-outlined text-slate-400 text-[16px]">trending_down</span>
            <div>
              <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Despesas</p>
              <p className="font-bold text-sm text-slate-700">{fmtBRL(expenses)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl">
            <span className="material-symbols-outlined text-slate-400 text-[16px]">confirmation_number</span>
            <div>
              <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Ticket Médio</p>
              <p className="font-bold text-sm text-slate-700">{ticket > 0 ? fmtBRL(ticket) : '—'}</p>
            </div>
          </div>
        </div>

        {/* Estimates */}
        {estTotal > 0 && (
          <div>
            <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-2">
              Orçamentos — {fmtBRL(unit.EstimatesTotalAmount)} ({estTotal} total)
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex items-center justify-between px-3 py-2 bg-emerald-50 rounded-lg">
                <span className="text-[11px] text-emerald-700 font-medium">Aprovados</span>
                <span className="text-[11px] font-bold text-emerald-700">{estApproved} · {fmtBRL(unit.EstimatesApprovedAmount)}</span>
              </div>
              <div className="flex items-center justify-between px-3 py-2 bg-amber-50 rounded-lg">
                <span className="text-[11px] text-amber-700 font-medium">Em aberto</span>
                <span className="text-[11px] font-bold text-amber-700">{estOpen} · {fmtBRL(unit.EstimatesOpenAmount)}</span>
              </div>
              {estFollowUp > 0 && (
                <div className="flex items-center justify-between px-3 py-2 bg-blue-50 rounded-lg">
                  <span className="text-[11px] text-blue-700 font-medium">Follow-up</span>
                  <span className="text-[11px] font-bold text-blue-700">{estFollowUp} · {fmtBRL(unit.EstimatesFollowUpAmount)}</span>
                </div>
              )}
              {estRejected > 0 && (
                <div className="flex items-center justify-between px-3 py-2 bg-red-50 rounded-lg">
                  <span className="text-[11px] text-red-700 font-medium">Rejeitados</span>
                  <span className="text-[11px] font-bold text-red-700">{estRejected} · {fmtBRL(unit.EstimatesRejectedAmount)}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Appointments */}
        {appTotal > 0 && (
          <div>
            <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-2">Consultas — {fmtNum(appTotal)} total</p>
            <div className="grid grid-cols-4 gap-2 text-center">
              <div className="p-2 bg-slate-50 rounded-lg">
                <p className="text-[9px] text-slate-400 font-bold uppercase">Finalizadas</p>
                <p className="font-black text-slate-700 text-sm">{fmtNum(appFinished)}</p>
              </div>
              <div className="p-2 bg-red-50 rounded-lg">
                <p className="text-[9px] text-red-400 font-bold uppercase">Perdidas</p>
                <p className="font-black text-red-600 text-sm">{fmtNum(appMissed)}</p>
              </div>
              <div className="p-2 bg-blue-50 rounded-lg">
                <p className="text-[9px] text-blue-400 font-bold uppercase">Novos</p>
                <p className="font-black text-blue-600 text-sm">{fmtNum(appNew)}</p>
              </div>
              <div className="p-2 bg-slate-50 rounded-lg">
                <p className="text-[9px] text-slate-400 font-bold uppercase">Retorno</p>
                <p className="font-black text-slate-700 text-sm">{fmtNum(appExisting)}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─────────── Main page ─────────── */
const ClinicorpReports = () => {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [columns, setColumns] = useState([]);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' | 'table'

  useEffect(() => {
    clinicorpApi.getAccounts()
      .then(r => {
        setAccounts(r.data.accounts || []);
        if (r.data.accounts?.length === 1) setSelectedAccount(r.data.accounts[0].id);
      })
      .catch(() => toast.error('Erro ao carregar contas Clinicorp'));
  }, []);

  const handleFetch = async () => {
    if (!selectedAccount) { toast.error('Selecione uma conta'); return; }
    if (!from || !to) { toast.error('Selecione o período'); return; }
    setLoading(true);
    setResults(null);
    try {
      const r = await clinicorpApi.getAnalytics(selectedAccount, from, to);
      const raw = r.data.results;
      const rows = Array.isArray(raw) ? raw
        : Array.isArray(raw?.data) ? raw.data
        : Array.isArray(raw?.results) ? raw.results
        : Array.isArray(raw?.items) ? raw.items
        : [];

      if (rows.length === 0) {
        setResults([]); setColumns([]);
        toast('Nenhum resultado para o período', { icon: 'ℹ️' });
        return;
      }

      const cols = Object.keys(rows[0]);
      setColumns(cols);
      setResults(rows);
      // auto-switch to cards if UnityName present, else table
      setViewMode(rows[0].UnityName !== undefined ? 'cards' : 'table');
      toast.success(`${rows.length} registros carregados`);
    } catch (err) {
      toast.error(err.message || 'Erro ao buscar dados Clinicorp');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    if (!results?.length) return;
    const acc = accounts.find(a => a.id === selectedAccount);
    const filename = `clinicorp_${acc?.name || selectedAccount}_${from}_${to}.csv`.replace(/\s/g, '_');
    exportCSV(columns, results, filename);
    toast.success('CSV exportado');
  };

  const handleExportJSON = () => {
    if (!results?.length) return;
    const acc = accounts.find(a => a.id === selectedAccount);
    const blob = new Blob([JSON.stringify(results, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clinicorp_${acc?.name || selectedAccount}_${from}_${to}.json`.replace(/\s/g, '_');
    a.click();
    URL.revokeObjectURL(url);
    toast.success('JSON exportado');
  };

  const hasUnityName = results?.length && results[0].UnityName !== undefined;

  // Totals for summary cards (numeric cols only)
  const totalRevenue = results?.reduce((s, r) => s + Number(r.TotalRevenueAmount || 0), 0) || 0;
  const totalReceived = results?.reduce((s, r) => s + Number(r.TotalReceivedAmount || 0), 0) || 0;
  const totalExpenses = results?.reduce((s, r) => s + Number(r.TotalExpenses || 0), 0) || 0;
  const totalEstApproved = results?.reduce((s, r) => s + Number(r.EstimatesApprovedAmount || 0), 0) || 0;
  const totalAppts = results?.reduce((s, r) => s + Number(r.AppointmentsTotal || 0), 0) || 0;

  // Generic totals for table view
  const totals = results?.length
    ? Object.fromEntries(
        columns
          .filter(c => isNumericKey(c) && results.every(r => !isNaN(parseFloat(r[c]))))
          .map(c => [c, results.reduce((s, r) => s + parseFloat(r[c] || 0), 0)])
      )
    : {};

  const selectedAcc = accounts.find(a => a.id === selectedAccount);

  return (
    <div className="animate-fade-in pb-16 pt-8 px-4 lg:px-8 max-w-7xl mx-auto w-full">
      {/* Header */}
      <header className="mb-8 flex justify-between items-center flex-wrap gap-4">
        <div>
          <h1 className="text-4xl lg:text-5xl font-extrabold font-headline tracking-tight text-on-surface mb-2">
            Relatórios Clinicorp
          </h1>
          <p className="text-secondary font-body">Analise e exporte resultados das clínicas integradas.</p>
        </div>
        {results?.length > 0 && (
          <div className="flex gap-2 flex-wrap">
            {hasUnityName && (
              <div className="flex items-center rounded-full border border-slate-200 overflow-hidden bg-white shadow-sm">
                <button
                  onClick={() => setViewMode('cards')}
                  className={`flex items-center gap-1.5 px-4 py-2 text-[12px] font-bold transition-colors ${viewMode === 'cards' ? 'bg-primary text-white' : 'text-slate-500 hover:bg-slate-50'}`}
                >
                  <span className="material-symbols-outlined text-[14px]">dashboard</span> Cards
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`flex items-center gap-1.5 px-4 py-2 text-[12px] font-bold transition-colors ${viewMode === 'table' ? 'bg-primary text-white' : 'text-slate-500 hover:bg-slate-50'}`}
                >
                  <span className="material-symbols-outlined text-[14px]">table_chart</span> Tabela
                </button>
              </div>
            )}
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full font-bold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors shadow-sm"
            >
              <span className="material-symbols-outlined text-sm">download</span> CSV
            </button>
            <button
              onClick={handleExportJSON}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full font-bold bg-slate-700 text-white hover:bg-slate-800 transition-colors shadow-sm"
            >
              <span className="material-symbols-outlined text-sm">data_object</span> JSON
            </button>
          </div>
        )}
      </header>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div className="md:col-span-2">
            <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Conta Clinicorp</label>
            {accounts.length === 0 ? (
              <div className="input flex items-center gap-2 text-slate-400">
                <span className="material-symbols-outlined text-sm animate-spin">refresh</span>
                Carregando contas...
              </div>
            ) : (
              <select className="input w-full" value={selectedAccount} onChange={e => setSelectedAccount(e.target.value)}>
                <option value="">Selecione uma conta...</option>
                {accounts.map(a => (
                  <option key={a.id} value={a.id}>{a.name} — {a.subscriberId}</option>
                ))}
              </select>
            )}
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">De</label>
            <input type="date" className="input w-full" value={from} onChange={e => setFrom(e.target.value)} />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Até</label>
            <input type="date" className="input w-full" value={to} onChange={e => setTo(e.target.value)} />
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {[
            { label: 'Mês Atual', action: () => { const d = new Date(); setFrom(new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0]); setTo(d.toISOString().split('T')[0]); } },
            { label: 'Últimos 7 dias', action: () => { const d = new Date(); const s = new Date(d); s.setDate(s.getDate() - 7); setFrom(s.toISOString().split('T')[0]); setTo(d.toISOString().split('T')[0]); } },
            { label: 'Últimos 14 dias', action: () => { const d = new Date(); const s = new Date(d); s.setDate(s.getDate() - 14); setFrom(s.toISOString().split('T')[0]); setTo(d.toISOString().split('T')[0]); } },
            { label: 'Últimos 30 dias', action: () => { const d = new Date(); const s = new Date(d); s.setDate(s.getDate() - 30); setFrom(s.toISOString().split('T')[0]); setTo(d.toISOString().split('T')[0]); } },
          ].map(({ label, action }) => (
            <button key={label} type="button" onClick={action}
              className="py-1.5 px-3 text-[12px] font-medium rounded-lg bg-slate-100 text-slate-600 hover:bg-primary/10 hover:text-primary transition-colors border border-transparent hover:border-primary/20"
            >{label}</button>
          ))}
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={handleFetch}
            disabled={loading || !selectedAccount}
            className="primary-pill-gradient text-white px-8 py-3 rounded-full font-bold shadow-lg hover:scale-105 transition-all flex items-center gap-2 disabled:opacity-50 disabled:hover:scale-100"
          >
            {loading
              ? <><span className="material-symbols-outlined animate-spin text-sm">refresh</span> Buscando...</>
              : <><span className="material-symbols-outlined text-sm">search</span> Buscar Resultados</>
            }
          </button>
        </div>
      </div>

      {/* Results */}
      {results !== null && (
        <>
          {/* Summary strip */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Unidades</p>
              <p className="font-headline font-black text-2xl text-slate-800">{results.length}</p>
            </div>
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Período</p>
              <p className="font-headline font-black text-base text-slate-800">{from} → {to}</p>
            </div>
            {hasUnityName && (
              <>
                <div className="primary-pill-gradient rounded-2xl p-5 shadow-lg shadow-primary/20 text-white">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/70 mb-1">Faturamento Total</p>
                  <p className="font-headline font-black text-xl">{fmtBRL(totalRevenue)}</p>
                </div>
                <div className="bg-emerald-600 rounded-2xl p-5 shadow-lg text-white">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/70 mb-1">Recebido Total</p>
                  <p className="font-headline font-black text-xl">{fmtBRL(totalReceived)}</p>
                </div>
                <div className={`rounded-2xl p-5 shadow-lg text-white ${totalReceived - totalExpenses >= 0 ? 'bg-blue-600' : 'bg-red-600'}`}>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/70 mb-1">Resultado Total</p>
                  <p className="font-headline font-black text-xl">{fmtBRL(totalReceived - totalExpenses)}</p>
                </div>
              </>
            )}
          </div>

          {results.length === 0 ? (
            <div className="bg-white rounded-2xl p-16 text-center border border-dashed border-slate-200 text-slate-400">
              <span className="material-symbols-outlined text-4xl mb-2 opacity-40">inbox</span>
              <p>Nenhum resultado para o período selecionado.</p>
            </div>
          ) : viewMode === 'cards' && hasUnityName ? (
            /* ── Cards view ── */
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {results
                .slice()
                .sort((a, b) => Number(b.TotalRevenueAmount || 0) - Number(a.TotalRevenueAmount || 0))
                .map((unit, i) => (
                  <UnitCard key={unit.UnityName || i} unit={unit} />
                ))}
            </div>
          ) : (
            /* ── Table view ── */
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100">
                <h3 className="font-headline font-bold flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary text-[20px]">table_chart</span>
                  Resultados ({results.length} registros)
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm font-body">
                  <thead className="bg-slate-50 text-slate-500 uppercase font-label tracking-widest text-[10px] sticky top-0">
                    <tr>
                      <th className="px-4 py-3 font-bold w-12">#</th>
                      {columns.map(col => (
                        <th key={col} className="px-4 py-3 font-bold whitespace-nowrap">{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {results.map((row, i) => (
                      <tr key={i} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3 text-slate-400 font-bold text-xs">{i + 1}</td>
                        {columns.map(col => (
                          <td key={col} className={`px-4 py-3 whitespace-nowrap ${isNumericKey(col) ? 'font-medium text-slate-800' : 'text-slate-600'}`}>
                            {formatCell(col, row[col])}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {Object.keys(totals).length > 0 && (
                      <tr className="bg-slate-100 font-bold border-t-2 border-slate-200">
                        <td className="px-4 py-3 text-slate-500 text-xs uppercase tracking-wider">Total</td>
                        {columns.map(col => (
                          <td key={col} className="px-4 py-3 whitespace-nowrap text-slate-800">
                            {totals[col] !== undefined ? fmtBRL(totals[col]) : ''}
                          </td>
                        ))}
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ClinicorpReports;
